import type { Tone } from './scan/outcome.ts';

/**
 * Perceptible confirmation of a scan.
 *
 * A decode takes a few dozen milliseconds and the screen is being read at arm's length, in
 * a noisy building, often through a glove. A line of text that is replaced by the next scan
 * a second later is not a confirmation an operator can rely on — so every scan is also
 * announced through two channels that do not compete for attention: the hand and the ear.
 *
 * The pattern differs per tone rather than being one generic buzz: the point is to be able
 * to keep walking, feel "that one was refused" and only then look at the phone.
 */

const PATTERNS: Record<Tone, number | number[]> = {
  ok: 45,
  neutral: [18, 70, 18],
  warn: [70, 90, 70],
  danger: [130, 80, 130, 80, 130],
};

export function haptic(tone: Tone): void {
  try {
    navigator.vibrate?.(PATTERNS[tone]);
  } catch {
    /* absent on desktop, and refused by some browsers without a gesture */
  }
}

/**
 * A blip per tone. Two rising notes for a success — one short beep is easy to miss in a
 * building full of machinery, and a rise is heard as "taken" where a fall is heard as
 * "refused" without anyone being taught the difference.
 */
const NOTES: Record<Tone, number[]> = {
  ok: [740, 1108],
  neutral: [600, 600],
  warn: [520, 400],
  danger: [400, 300, 220],
};

let ctx: AudioContext | null = null;
let unlocked = false;

function audioCtor(): typeof AudioContext | undefined {
  if (typeof window === 'undefined') return undefined;
  return (
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  );
}

/**
 * Start, or restart, the audio context.
 *
 * A browser only lets this happen off the back of a user gesture, and the call must happen
 * *during* the handler — priming it later, from an effect that runs once the component has
 * mounted, leaves the context `suspended` on iOS and the cue never fires. So this is called
 * from the buttons themselves, and once at the first touch anywhere (see `unlockAudio`).
 */
export function primeAudio(): void {
  try {
    const Ctor = audioCtor();
    if (!Ctor) return;
    ctx ??= new Ctor();
    // A context can also be suspended again by the system after a call or a lock screen.
    if (ctx.state !== 'running') void ctx.resume?.();
  } catch {
    ctx = null;
  }
}

/**
 * The first touch anywhere in the app starts the audio context, so the very first scan of a
 * session already beeps. Installed once at boot; it removes itself after firing.
 */
export function unlockAudio(): void {
  if (unlocked || typeof window === 'undefined') return;
  unlocked = true;
  const start = () => {
    primeAudio();
    window.removeEventListener('pointerdown', start);
    window.removeEventListener('keydown', start);
  };
  window.addEventListener('pointerdown', start, { once: true, passive: true });
  window.addEventListener('keydown', start, { once: true });
}

/** What Réglages shows, so a dead cue is visible instead of silently absent. */
export function audioState(): 'running' | 'suspended' | 'unsupported' {
  if (!audioCtor()) return 'unsupported';
  return ctx?.state === 'running' ? 'running' : 'suspended';
}

function play(tone: Tone): void {
  if (!ctx) return;
  const now = ctx.currentTime;
  NOTES[tone].forEach((hz, i) => {
    const at = now + i * 0.085;
    const osc = ctx!.createOscillator();
    const gain = ctx!.createGain();
    osc.type = 'square';
    osc.frequency.setValueAtTime(hz, at);
    // An envelope, not a raw on/off: an abrupt edge clicks and sounds like a fault.
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.exponentialRampToValueAtTime(0.2, at + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.11);
    osc.connect(gain).connect(ctx!.destination);
    osc.start(at);
    osc.stop(at + 0.12);
  });
}

/**
 * Square wave rather than sine: it carries over machine noise at a volume that does not
 * make the phone itself unpleasant to hold.
 *
 * A suspended context is resumed and then played, instead of being treated as "no sound".
 * Dropping the cue silently is the worst outcome available here: the operator keeps
 * trusting a confirmation that never comes.
 */
export function chime(tone: Tone): void {
  try {
    primeAudio();
    if (!ctx) return;
    if (ctx.state === 'running') {
      play(tone);
      return;
    }
    void ctx.resume?.().then(() => play(tone)).catch(() => undefined);
  } catch {
    /* an unavailable output must never break a scan */
  }
}

/** How long a scan result stays on screen before the camera takes the next one. */
export const HOLD_PRESETS = [
  { ms: 900, label: 'Rapide' },
  { ms: 1800, label: 'Normal' },
  { ms: 3000, label: 'Lent' },
] as const;
